import { defineStore, acceptHMRUpdate } from "pinia";

interface State {
  linkToken: string | null;
}

export const useLineStore = defineStore({
  id: "line",

  state: (): State => ({
    linkToken: null,
  }),

  // sessionStorage, where every other persisted store here uses localStorage.
  // Deliberate: this token belongs to one LINE flow, not to the visitor. With
  // no server-side record left to revoke, the only other bound on it is
  // Line::LINK_TOKEN_TTL, so narrowing the storage to one tab is the second
  // half of the defence #1660 §3.2 asks for ("別セッションへの誤紐付けを防ぐ").
  // It also means a dead token cannot linger and get attached to every later
  // request, which localStorage would allow.
  //
  // ACCEPTED RISK — a LINE login on mobile can hand control to the LINE app
  // and come back to a different webview than it started in. A tab-scoped
  // token does not survive that: the visitor returns, nothing is prefilled,
  // nothing is linked, and no message explains why. Retrying fails the same
  // way. This is NOT the same as "closing the tab means logging in again";
  // the visitor closes nothing. Verify the in-app browser path on a real
  // device before release — if it drops the token, this must become
  // window.localStorage.
  persist: {
    key: "united-cart-line",
    storage: window.sessionStorage,
    paths: ["linkToken"],
  },

  actions: {
    // Counterpart to LineController#write_link_token_and_handoff. The token is
    // injected into the page by the handoff destination
    // (layouts/customer.html.erb, functions/_set_global_variables.html.erb)
    // and dropped from `window` as soon as it is in the store, so nothing on
    // the page can read it back out afterwards.
    adoptHandoffToken() {
      const token = window.lineLinkToken;

      if (token) {
        this.linkToken = token;
        delete window.lineLinkToken;
      }

      this.dropStaleToken();
    },

    // LineController used to delete the link cookie on every terminal branch
    // of #resolve_link - already linked, linked just now, signed in - so the
    // browser would stop resending something nothing would ever act on.
    // Those branches announce themselves in the query string, so this is the
    // same cleanup performed where the token now lives.
    dropStaleToken() {
      const params = new URLSearchParams(window.location.search);

      if (params.get("line_linked") === "1" || params.get("line_login") === "1") this.clear();
    },

    clear() {
      this.linkToken = null;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useLineStore, import.meta.hot));
}
